import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { State } from "../../app/store";
import { isAddToBasketInfoMessage } from "../../app/utils/apiInfoMessages";
import "./basketToastStack.scss";

const MAX_TOASTS = 3;
const TOAST_DURATION_MS = 5000;
const EXIT_MS = 220;

interface BasketToast {
  id: number;
  message: string;
  leaving: boolean;
}

interface IProps {
  info: string[] | null;
  onConsumed: Function;
}

/**
 * Deck of "added to basket" toasts. The newest card sits in front.
 * Older cards scale down and peek out underneath, up to three.
 * Only the front card runs the countdown. When it leaves, the next one
 * comes forward and starts a fresh bar.
 */
const BasketToastStack: React.FC<IProps> = ({ info, onConsumed }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const showPopupAfterAddTicket = useSelector((state: State) => state.config.configuration.popupAfterAddTicket);
  const [toasts, setToasts] = useState<BasketToast[]>([]);
  const [frontHeight, setFrontHeight] = useState<number>();
  const nextId = useRef(1);
  const consumedInfo = useRef<string[] | null>(null);
  const frontRef = useRef<HTMLElement>(null);

  const activeToasts = toasts.filter((toast) => !toast.leaving);
  const frontToast = activeToasts[0];

  useEffect(() => {
    if (!info?.length) {
      consumedInfo.current = null;
      return;
    }
    if (consumedInfo.current === info) return;
    if (showPopupAfterAddTicket) return;
    if (location.pathname.includes("/finalize")) return;

    const message = info.filter(isAddToBasketInfoMessage).join(" ").replace(/\s+/g, " ").trim();
    if (!message) return;

    consumedInfo.current = info;
    const id = nextId.current++;
    setToasts((prev) => {
      const leaving = prev.filter((toast) => toast.leaving);
      const active = prev.filter((toast) => !toast.leaving);
      return [...leaving, { id, message, leaving: false }, ...active].slice(0, leaving.length + MAX_TOASTS);
    });
    if (info.every(isAddToBasketInfoMessage)) {
      onConsumed();
    }
  }, [info, location.pathname, onConsumed, showPopupAfterAddTicket]);

  useEffect(() => {
    if (!frontToast) return;
    const timeout = window.setTimeout(() => {
      setToasts((prev) => prev.map((toast) => (toast.id === frontToast.id ? { ...toast, leaving: true } : toast)));
    }, TOAST_DURATION_MS);
    return () => window.clearTimeout(timeout);
  }, [frontToast]);

  useEffect(() => {
    if (!toasts.some((toast) => toast.leaving)) return;
    const timeout = window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => !toast.leaving));
    }, EXIT_MS);
    return () => window.clearTimeout(timeout);
  }, [toasts]);

  useLayoutEffect(() => {
    const node = frontRef.current;
    if (!node) {
      setFrontHeight(undefined);
      return;
    }
    setFrontHeight(node.offsetHeight);
  }, [frontToast?.id, frontToast?.message]);

  const dismiss = (id: number) => {
    setToasts((prev) => prev.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast)));
  };

  if (toasts.length === 0) return null;

  let activeIndex = 0;

  return (
    <div
      className="basket-toast-stack"
      style={{ ["--front-height" as string]: frontHeight ? `${frontHeight}px` : undefined }}
      aria-live="polite"
    >
      {toasts.map((toast) => {
        const index = toast.leaving ? 0 : activeIndex++;
        const isFront = !toast.leaving && index === 0;

        return (
          <article
            key={toast.id}
            ref={isFront ? frontRef : undefined}
            className={`basket-toast${isFront ? " is-front" : ""}${toast.leaving ? " is-leaving" : " is-entering"}`}
            style={{ ["--stack-index" as string]: index }}
            role={isFront ? "status" : undefined}
            aria-hidden={isFront ? undefined : true}
            onClick={isFront ? () => dismiss(toast.id) : undefined}
          >
            <div className="basket-toast__content">
              <h4 className="basket-toast__title">{t("informacja")}</h4>
              <p className="basket-toast__message">{toast.message}</p>
              {isFront && (
                <div className="basket-toast__timer" aria-hidden="true">
                  <span
                    key={toast.id}
                    className="basket-toast__timer-fill"
                    style={{ animationDuration: `${TOAST_DURATION_MS}ms` }}
                  />
                </div>
              )}
            </div>
            {isFront && (
              <button
                type="button"
                className="basket-toast__close"
                aria-label={t("zamknij")}
                onClick={(event) => {
                  event.stopPropagation();
                  dismiss(toast.id);
                }}
              />
            )}
          </article>
        );
      })}
    </div>
  );
};

export default BasketToastStack;
